import { Component } from 'react';
import classNames from 'classnames';
import ReactCSSTransitionGroup from 'react-addons-css-transition-group';
import { NullStateCard } from '@hc/component-lib/hclib/components/molecules/null-state-card';
import { LoadingIconSimple } from '@hc/component-lib/hclib/components/atoms/icons';
import { HorizontalToggle } from '@hc/component-lib/hclib/components/atoms/horizontal-toggle';
import { Chart } from '@hc/chart-dot-jsx';

import { NULL_STATE_WARNINGS } from 'legacy/appstore/constants';

import { dollarsFormatter } from 'legacy/utils/formatters';
import { capitalizeFirstLetter } from 'legacy/utils/strings';

import Loading from 'legacy/components/generic/Loading';

import { PropertyExplorerForecastChart as theme } from 'legacy/css-modules';

const SUBHEADINGS = {
  zip: 'Our Zip-Code-level forecast shows how this home is likely to appreciate in the next 36 months based on the rate of home price growth in the Zip-Code.',
  block:
    'Our block-level forecast shows how this home is likely to appreciate in the next 12 months based on the rate of home price growth on the block.'
};

const ForecastCallout = ({ yr, month, pctDisplay, pctDiff, value }) => {
  let className =
    pctDiff < 0 ? theme.ForecastValueNegative : theme.ForecastValuePositive;
  return (
    <div data-hc-name="forecast-chart-label">
      <div className={theme.ForecastValueLabel}>{yr} Year</div>
      <div
        id={`forecast-${month}-pct`}
        className={classNames(theme.ForecastValue, className)}
      >
        {pctDisplay}
      </div>
      <div className={theme.ForcastValueDollars}>{dollarsFormatter(value)}</div>
    </div>
  );
};

const ForecastCallouts = ({ data, keyPrefix, renderIdx, style }) => {
  if (!data || !data.length) {
    return null;
  }
  return (
    <div>
      <ReactCSSTransitionGroup
        component="div"
        className={theme.ForecastCallouts}
        transitionName="zoomInDown"
        transitionEnterTimeout={0}
        transitionLeave={false}
        transitionEnter
      >
        {data.map((p, i) => {
          if (i <= renderIdx) {
            return (
              <ForecastCallout
                key={`${keyPrefix}-${i}`}
                yr={i + 1}
                month={data[i].month}
                pctDisplay={data[i].pctDisplay}
                pctDiff={data[i].pctDiff}
                value={data[i].y}
              />
            );
          } else {
            return null;
          }
        })}
      </ReactCSSTransitionGroup>
    </div>
  );
};

class PropertyExplorerForecastChart extends Component {
  state = {
    renderIdx: -1,
    prevRenderAreaType: null
  };
  delayedRenderTimeouts = [];

  componentDidMount() {
    const { calloutData } = this.props;
    if (calloutData && calloutData.length) {
      this.delayedRender(calloutData);
    }
  }

  componentWillUpdate(nextProps) {
    const { calloutData, areaType } = this.props;
    if (
      ((!calloutData || !calloutData.length) &&
        nextProps.calloutData &&
        nextProps.calloutData.length) ||
      (areaType !== nextProps.areaType &&
        nextProps.calloutData &&
        nextProps.calloutData.length)
    ) {
      this.delayedRender(nextProps.calloutData);
      if (areaType !== nextProps.areaType) {
        this.setState({
          prevRenderAreaType: areaType,
          renderIdx: -1
        });
      }
    }
  }

  componentWillUnmount() {
    for (var timeout in this.delayedRenderTimeouts) {
      window.clearTimeout(timeout);
    }
  }

  delayedRender = (data) => {
    let i = 0;
    let r = data.length;
    while (i < r) {
      this.delayedRenderTimeouts.push(
        window.setTimeout(this.updateRenderIdx, 200 * (i + 1))
      );
      i++;
    }
  };

  updateRenderIdx = () => {
    const { renderIdx } = this.state;
    this.setState({
      renderIdx: renderIdx + 1
    });
  };

  render() {
    const {
      chartConfig,
      hasErrored,
      hasDataBlock,
      hasDataZip,
      isLoading,
      areaType,
      calloutData,
      handleChangeAreaType
    } = this.props;

    const { renderIdx, prevRenderAreaType } = this.state;

    if (hasErrored || (!hasDataBlock && !hasDataZip)) {
      return (
        <div className={theme.wrapper} data-hc-name="forecast-chart">
          <NullStateCard
            className={theme.NullStateCard}
            title={NULL_STATE_WARNINGS.forecastSection.title}
          >
            {NULL_STATE_WARNINGS.forecastSection.message}
          </NullStateCard>
        </div>
      );
    }
    return (
      <div className={theme.wrapper} data-hc-name="forecast-chart">
        <Loading
          loading={isLoading}
          Spinner={LoadingIconSimple}
          spinnerClassName={theme.LoadingSpinner}
          overlay
          spinnerDataHcName="forecast-chart-spinner"
        >
          <div>
            <div className={theme.dataToggle}>
              <HorizontalToggle
                dataHcName="forecast-chart-toggle"
                value={areaType}
                options={[
                  {
                    value: 'zip',
                    label: 'Zip-Code',
                    dataHcName:
                      areaType === 'zip'
                        ? 'forecast-chart-toggle-selected'
                        : 'forecast-chart-toggle-unselected'
                  },
                  {
                    value: 'block',
                    label: 'Block',
                    dataHcName:
                      areaType === 'block'
                        ? 'forecast-chart-toggle-selected'
                        : 'forecast-chart-toggle-unselected'
                  }
                ]}
                onSelect={(newAreaType) =>
                  handleChangeAreaType(
                    newAreaType,
                    newAreaType === 'zip' ? hasDataZip : hasDataBlock
                  )
                }
              />
            </div>
            <div
              className={theme.subHeading}
              data-hc-name="forecast-chart-subheader"
            >
              {SUBHEADINGS[areaType]}
            </div>
          </div>
          <div className={theme.PropertyExplorerForecast}>
            <div className={theme.PropertyExplorerForecastChart}>
              {(areaType === 'zip' && hasDataZip) ||
              (areaType === 'block' && hasDataBlock) ? (
                <Chart chartConfig={chartConfig} />
              ) : (
                <NullStateCard
                  className={theme.NullStateCard}
                  title={
                    NULL_STATE_WARNINGS[
                      `forecastChart${capitalizeFirstLetter(areaType)}`
                    ].title
                  }
                >
                  {
                    NULL_STATE_WARNINGS[
                      `forecastChart${capitalizeFirstLetter(areaType)}`
                    ].message
                  }
                </NullStateCard>
              )}
              <ForecastCallouts
                keyPrefix={prevRenderAreaType}
                renderIdx={renderIdx}
                data={calloutData}
              />
            </div>
          </div>
        </Loading>
      </div>
    );
  }
}

export default PropertyExplorerForecastChart;
